import Map from "ol/Map";
import View from "ol/View";
import Extent from "ol/interaction/Extent";
import TileLayer from "ol/layer/Tile";
import { transform, transformExtent } from "ol/proj";
import { register } from "ol/proj/proj4";
import Style from "ol/style/Style";
import OSM from "ol/source/OSM";
import proj4 from "proj4";
import { shiftKeyOnly } from "ol/events/condition";
import { defaults, FullScreen } from "ol/control";
import { useEffect, useRef, useState } from "react";
import {
  Inline,
  Field,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Box,
  Backdrop,
  Text,
} from "@digitalreality/ui";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import React from "react";
import { useAppState } from "../../StateStore/Store";
import { EPSGCodes, Unit, ModeModelErrorType } from "../../Helpers/Constants";
import { Settings } from "../../Models/Settings";
import Stroke from "ol/style/Stroke";
import { ProjectFormData } from "../../Models/ProjectFormData";
import { getProjection } from "../../Services/MapService";
import { RestApiResponse } from "../../Models/RestApiResponse";
import { notAuthorised } from "../../StateStore/UserAuth/UserAuthActions";
import { MapProjection } from "../../Models/MapProjection";
import { CheckboxCheckedIcon } from "@digitalreality/ui-icons";
import IconButton from "@mui/material/IconButton";
import i18next from "i18next";
const Geocoder = require("ol-geocoder");
require("ol-geocoder/dist/ol-geocoder.min.css");
export default function LayersMap({ onSubmit, formRef }: any) {
  const { state, dispatch } = useAppState();
  const { t } = useTranslation();

  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<Map>();

  const [mapExtentPoints, setMapExtentPoints] = useState<number[]>([]);
  const [otherEpsg, setOtherEpsg] = useState<boolean>();
  const [otherEpsgValue, setOtherEpsgValue] = useState<number>(0);
  const [epsgCodeValidated, setEpsgCodeValidated] = useState<boolean>(false);
  const [mapReload, setMapReload] = useState<boolean>(false);

  useEffect(() => {
    return getInitialMapValues();
  }, []);

  const {
    control,
    formState: { errors },
    setValue,
    getValues,
    setError,
    clearErrors,
    handleSubmit,
  } = useForm({
    mode: "onBlur",
    defaultValues: state.projectModal.project,
  });

  const onLocalSubmit = async (data: ProjectFormData) => {
    if (otherEpsg) {
      data.epsgCode = otherEpsgValue;

      if (!epsgCodeValidated) {
        setError("epsgCode", {
          type: "Validate",
          message: t("Texts.EPSGCodeNotValidated"),
        });
        return;
      }
    }

    if (!data.epsgCode) {
      setError("epsgCode", {
        type: "required",
        message: t("Texts.ValidationRequired") as string,
      });
      return;
    }

    if (mapExtentPoints.length === 4) {
      var projectSettings = JSON.parse(getValues("settings") ?? "") as Settings;

      projectSettings.Map.MaxExtent = mapExtentPoints;
      projectSettings.Map.StartExtent = mapExtentPoints;

      data.settings = JSON.stringify(projectSettings);
    }

    return onSubmit(data);
  };

    const validateEpsg = (epsgCode: number) => {
        setMapReload(true);
        getProjection(epsgCode).then((response: RestApiResponse) => {
            console.log(response)
            if (response.statusCode === 401) {
                dispatch(notAuthorised());
            }
            if (response.success) {
                clearErrors("epsgCode");
                setEpsgCodeValidated(true);
                const data = response.data as MapProjection;
                const extent = [data.Xmin, data.Ymin, data.Xmax, data.Ymax];
                if (map) {
                    setMapExtent(map, epsgCode, data.proj4js, extent);
                }

                var settings: Settings = {
                    Map: {
                        Proj4JsDefinition: data.proj4js,
                        MaxExtent: extent,
                        StartExtent: extent,
                    },
                };
                setValue("settings", JSON.stringify(settings));
            } else {
                setError("epsgCode", {
                    type: "isValid",
                    message: t("Texts.EPSGCodeValidation"),
                });

                if (map) {
                    deleteMapExtent(map);
                    setMapExtentPoints([]);
                }
            }

            setMapReload(false);
        });
    };

  const getInitialMapValues = () => {
    var projectSettings = state.projectModal.project?.settings
      ? (JSON.parse(state.projectModal.project?.settings) as Settings)
      : null;

    var centerPoint = transform([7.994121, 47.53122], "EPSG:4326", "EPSG:3857");

    const initialMap = new Map({
      target: mapRef.current as HTMLDivElement,

      layers: [
        new TileLayer({
          source: new OSM(),
        }),
      ],
      view: new View({
        center: centerPoint,
        zoom: 2,
      }),
      controls: defaults({ attribution: false }).extend([
        // Add a fullscreen button control to the map.
        new FullScreen(),
      ]),
    });
    setMap(initialMap);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(function (position) {
        centerPoint = transform(
          [position.coords.longitude, position.coords.latitude],
          "EPSG:4326",
          "EPSG:3857"
        );
      });
    }

    initialMap.setView(
      new View({
        center: centerPoint,
        zoom: 2,
      })
    );

    const geocoder = new Geocoder("nominatim", {
      provider: "osm",
      lang: i18next.language,
      placeholder: t("Texts.SearchText"),
      targetType: "text-input",
      limit: 5,
      keepOpen: false,
    });

    initialMap.addControl(geocoder);
    // hide pin point
    geocoder.getLayer().setVisible(false);

    if (
      state.projectModal.project?.epsgCode &&
      projectSettings &&
      projectSettings.Map.Proj4JsDefinition &&
      projectSettings.Map.MaxExtent
    ) {
      if (
        !EPSGCodes.some(
          (epsg) => epsg.code === state.projectModal.project?.epsgCode
        )
      ) {
        setOtherEpsg(true);
        setOtherEpsgValue(state.projectModal.project?.epsgCode);
        setEpsgCodeValidated(true);
      }

      setMapExtent(
        initialMap,
        state.projectModal.project.epsgCode,
        projectSettings.Map.Proj4JsDefinition,
        projectSettings.Map.MaxExtent
      );
    }

    return () => initialMap.setTarget();
  };

  const setMapExtent = (
    map: Map,
    epsgCode: number,
    proj4Def: string,
    mapExtentPoints: number[]
  ) => {
    if (epsgCode !== 3857) {
      proj4.defs("EPSG:" + epsgCode, proj4Def);
      register(proj4);
    }
    var extentPoints = transformExtent(
      mapExtentPoints,
      "EPSG:" + epsgCode,
      "EPSG:3857"
    );

    var extent = new Extent({
      extent: extentPoints,
      condition: shiftKeyOnly,
      boxStyle: new Style({
        stroke: new Stroke({
          color: [0, 0, 255],
          width: 2,
        }),
      }),
    });
    extent.on("extentchanged", function (event) {
      if (event.extent) {
        var newExtent = transformExtent(
          event.extent,
          "EPSG:3857",
          "EPSG:" + epsgCode
        );
        setMapExtentPoints(newExtent);
      }
    });

    deleteMapExtent(map);

    map.addInteraction(extent);
    map.getView().fit(extentPoints, {
      size: map.getSize(),
      padding: [100, 100, 100, 100],
    });

    setMapExtentPoints(mapExtentPoints);
  };

  const deleteMapExtent = (map: Map) => {
    map.getInteractions().forEach((iteraction) => {
      if (iteraction instanceof Extent) {
        map.removeInteraction(iteraction);
      }
    });
  };

  const displayMapExtentPoints = (extentPoints: number[]) => {
    return extentPoints.map((point) => Math.round(point)).join(" ");
  };

  const getDefaultEpsgCode = () => {
    return getValues("epsgCode") !== undefined
      ? EPSGCodes.some(
          (epsg) => epsg.code === (getValues("epsgCode") as number)
        )
        ? getValues("epsgCode")
        : "Other"
      : "";
  };

  return (
    <form
      id="mapForm"
      onSubmit={handleSubmit(onLocalSubmit)}
      ref={formRef}
      autoComplete="off"
    >
      <Inline gap={4} alignItems="flex-start">
        <Stack gap={6} alignItems="flex-start" minWidth="300px">
          <FormControl error={!!errors.epsgCode}>
            <InputLabel>{t("Texts.EPSGCode")}</InputLabel>
            <Select
              label={"EPSG Code"}
              defaultValue={getDefaultEpsgCode}
              onChange={(event) => {
                clearErrors("epsgCode");
                if (event.target.value === "Other") {
                  setOtherEpsg(true);
                  setEpsgCodeValidated(false);
                  setValue("epsgCode", undefined);
                  if (map) deleteMapExtent(map);
                } else {
                  setOtherEpsg(false);
                  setValue("epsgCode", +event.target.value);
                  validateEpsg(+event.target.value);
                }
              }}
            >
              {EPSGCodes.map((epsg) => (
                <MenuItem key={epsg.code} value={epsg.code}>
                  {epsg.code} - {epsg.country} - {epsg.name}
                </MenuItem>
              ))}
              <MenuItem key="Other" value="Other">
                {t("Texts.Other")}
              </MenuItem>
            </Select>
            <FormHelperText>{errors.epsgCode?.message}</FormHelperText>
          </FormControl>
          {otherEpsg && (
            <Inline>
              <Field
                type="number"
                label={t("Texts.Other")}
                defaultValue={getValues("epsgCode")}
                error={!!errors.epsgCode}
                helperText={errors.epsgCode?.message}
                onChange={(event) =>
                  setOtherEpsgValue(+event.currentTarget.value)
                }
              />
              <IconButton onClick={() => validateEpsg(otherEpsgValue)}>
                <CheckboxCheckedIcon />
              </IconButton>
            </Inline>
          )}
          <Controller
            name="unit"
            control={control}
            rules={{ required: t("Texts.ValidationRequired") as string }}
            render={({ field }) => (
              <FormControl error={!!errors.unit}>
                <InputLabel>{t("Texts.Unit")}</InputLabel>
                <Select
                  {...field}
                  label={t("Texts.Unit")}
                  onChange={(event) => {
                    setValue("unit", event.target.value as number);
                  }}
                >
                  <MenuItem key={Unit.Meter} value={Unit.Meter}>
                    {t("Texts.Unit_meter")}
                  </MenuItem>
                  <MenuItem key={Unit.Foot} value={Unit.Foot}>
                    {t("Texts.Unit_foot")}
                  </MenuItem>
                </Select>
                <FormHelperText>{errors.unit?.message}</FormHelperText>
              </FormControl>
            )}
          />
          <Controller
            name="modeModelError"
            control={control}
            render={({ field }) => (
              <FormControl>
                <InputLabel>{t("Texts.ModeModelError")}</InputLabel>
                <Select
                  {...field}
                  label={t("Texts.ModeModelError")}
                  onChange={(event) => {
                    setValue("modeModelError", event.target.value as number);
                  }}
                >
                  {Object.keys(ModeModelErrorType).map(
                    (key) =>
                      isFinite(+key) && (
                        <MenuItem key={key} value={+key}>
                          {t("Texts." + ModeModelErrorType[+key])}
                        </MenuItem>
                      )
                  )}
                </Select>
              </FormControl>
            )}
          />
          <Field
            label={t("Texts.MapExtent")}
            disabled
            value={displayMapExtentPoints(mapExtentPoints)}
          />
        </Stack>
        <Stack gap={3}>
          <Box position="relative" width="980px">
            <Backdrop open={mapReload} sx={{ position: "absolute", zIndex: 1 }}>
              <CircularProgress />
            </Backdrop>
            <div ref={mapRef} style={{ width: "980px", height: "530px" }}></div>
          </Box>
          <Text color="#aaaaaa" style={{marginLeft:"100px", width: "780px", textAlign: "center"}}>{t("Texts.MapExtentInfo")}</Text>
        </Stack>
      </Inline>
    </form>
  );
}
